import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useEmpresa } from "../contexts/EmpresaContext";
import { toast } from "sonner";
import { prefetchAllForEmpresa, prefetchBundles } from "../utils/prefetchData";
import VoiceMode from "../components/VoiceMode";
import {
  Sparkles,
  Building2,
  TrendingUp,
  Users,
  Package,
  ArrowRight,
  Sun,
  Moon,
  Coffee,
  Send,
  AlertTriangle,
  FileText,
  Download,
  X,
  ChevronDown,
  Zap,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Square,
  AudioWaveform,
  Car
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const { user, token } = useAuth();
  const { empresas, cambiarEmpresa, fetchEmpresas, loading: empresaLoading } = useEmpresa();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [empresasData, setEmpresasData] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [conversacion, setConversacion] = useState([]);
  const [loadingIA, setLoadingIA] = useState(false);
  const [alertas, setAlertas] = useState([]);
  const [showAlertas, setShowAlertas] = useState(false);
  const chatEndRef = useRef(null);
  
  // Voice states
  const [isRecording, setIsRecording] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [showVoiceMode, setShowVoiceMode] = useState(false);
  const [isListening, setIsListening] = useState(false); // Para el botón de micrófono inline
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentAudioRef = useRef(null);
  const recognitionRef = useRef(null); // Para Speech Recognition

  // Saludo según hora del día
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return { text: "Buenos días", icon: Coffee, emoji: "☀️" };
    if (hour < 19) return { text: "Buenas tardes", icon: Sun, emoji: "🌤️" };
    return { text: "Buenas noches", icon: Moon, emoji: "🌙" };
  };

  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;

  // Cargar historial de localStorage
  useEffect(() => {
    const savedConversacion = localStorage.getItem("edmundo_chat_history");
    if (savedConversacion) {
      try {
        const parsed = JSON.parse(savedConversacion);
        // Solo cargar si es del mismo día
        const today = new Date().toDateString();
        if (parsed.date === today && parsed.messages) {
          setConversacion(parsed.messages);
        }
      } catch (e) {
        console.error("Error loading chat history", e);
      }
    }
  }, []);

  // Guardar historial
  useEffect(() => {
    if (conversacion.length > 0) {
      localStorage.setItem("edmundo_chat_history", JSON.stringify({
        date: new Date().toDateString(),
        messages: conversacion.slice(-20) // Mantener últimos 20 mensajes
      }));
    }
  }, [conversacion]);

  // Scroll automático al nuevo mensaje
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversacion]);

  // Mensaje de bienvenida simple (sin llamada a IA)
  useEffect(() => {
    if (conversacion.length === 0 && !empresaLoading) {
      const hora = new Date().getHours();
      let saludo = hora < 12 ? "Buenos días" : hora < 19 ? "Buenas tardes" : "Buenas noches";
      setConversacion([{
        tipo: "asistente",
        texto: `¡${saludo}, ${user?.name || ""}! 👋 Soy tu asistente inteligente. ¿En qué puedo ayudarte hoy? Puedes preguntarme sobre ventas, inventario, clientes o pedirme recomendaciones para tu negocio.`
      }]);
    }
  }, [conversacion.length, empresaLoading, user?.name]);

  // Cargar métricas cuando las empresas estén listas
  useEffect(() => {
    const loadMetricas = async () => {
      if (empresas.length === 0) return;
      
      setLoading(true);
      try {
        const empresasConMetricas = await Promise.all(
          empresas.map(async (empresa) => {
            try {
              const res = await axios.get(`${API}/dashboard/metricas?empresa_id=${empresa.id}`);
              return { ...empresa, metricas: res.data };
            } catch (e) {
              return { ...empresa, metricas: null };
            }
          })
        );
        setEmpresasData(empresasConMetricas);
      } catch (error) {
        console.error("Error loading metricas:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadMetricas();
  }, [empresas]);

  // Pre-carga bundles JS + datos de módulos en background (elimina spinners en primera visita)
  useEffect(() => {
    if (empresas.length === 0) return;
    const run = () => {
      prefetchBundles();
      empresas.forEach(emp => {
        prefetchAllForEmpresa(emp.id);
      });
    };
    // Esperar 1.5s para que la página de inicio termine de cargar primero
    const timer = setTimeout(() => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 3000 });
      } else {
        run();
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [empresas]);

  const handleSelectEmpresa = (empresa) => {
    cambiarEmpresa(empresa.id);
    toast.success(`Entrando a ${empresa.nombre}`);
    // Dar tiempo para que el estado se actualice antes de navegar
    setTimeout(() => navigate("/dashboard"), 200);
  };

  const handleAsistenteSubmit = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;

    const pregunta = userInput;
    setUserInput("");
    setConversacion(prev => [...prev, { tipo: "user", texto: pregunta }]);
    setLoadingIA(true);

    try {
      const response = await axios.post(`${API}/asistente-ia`, {
        pregunta: pregunta,
        empresa_id: null,
        historial: conversacion.slice(-10)
      });
      
      setConversacion(prev => [...prev, { 
        tipo: "asistente", 
        texto: response.data.respuesta 
      }]);
      
      // Manejar navegación automática
      if (response.data.accion?.tipo === "navegar") {
        const ruta = response.data.accion.ruta;
        const destino = response.data.accion.destino;
        
        // Si es una ruta de empresa, primero seleccionar la empresa
        if (ruta !== "/" && empresas.length > 0) {
          cambiarEmpresa(empresas[0].id);
        }
        
        setTimeout(() => {
          toast.success(`Yendo a ${destino}...`);
          navigate(ruta);
        }, 1500);
      }
      
    } catch (error) {
      console.error("Error en asistente:", error);
      setConversacion(prev => [...prev, { 
        tipo: "asistente", 
        texto: "Disculpá, tuve un problema. ¿Podés intentar de nuevo?" 
      }]);
    } finally {
      setLoadingIA(false);
    }
  };

  const handleGenerarReporte = async (empresaId) => {
    toast.loading("Generando reporte PDF...");
    try {
      // Usar el mismo endpoint que el Reporte Diario del panel
      const fecha = new Date().toISOString().split('T')[0];
      const response = await axios.get(
        `${API}/eco-reporte/diario/pdf?empresa_id=${empresaId}&fecha=${fecha}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ReporteDiario_${fecha}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.dismiss();
      toast.success("Reporte descargado");
    } catch (error) {
      toast.dismiss();
      toast.error("Error al generar reporte");
    }
  };

  const handleAlertaAccion = (alerta) => {
    if (alerta.accion) {
      const rutas = {
        "ir_inventario": "/inventario",
        "ir_ventas": "/ventas",
        "ir_clientes": "/clientes",
        "ir_compras": "/compras",
        "ir_logistica": "/logistica"
      };
      
      if (empresas.length > 0) {
        cambiarEmpresa(empresas[0].id);
      }
      navigate(rutas[alerta.accion] || "/dashboard");
    }
    setShowAlertas(false);
  };

  const clearChat = () => {
    localStorage.removeItem("edmundo_chat_history");
    setConversacion([]);
    
    // Recrear mensaje de bienvenida
    const hora = new Date().getHours();
    let saludo = hora < 12 ? "Buenos días" : hora < 19 ? "Buenas tardes" : "Buenas noches";
    setConversacion([{
      tipo: "asistente",
      texto: `¡${saludo}, ${user?.name || ""}! 👋 Soy tu asistente inteligente. ¿En qué puedo ayudarte hoy? Puedes preguntarme sobre ventas, inventario, clientes o pedirme recomendaciones para tu negocio.`
    }]);
    
    toast.success("Conversación reiniciada");
  };

  // ==================== VOICE FUNCTIONS ====================
  
  // Start recording voice
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await processVoiceInput(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      toast.info("Grabando... Habla ahora");
    } catch (error) {
      console.error("Error accessing microphone:", error);
      toast.error("No se pudo acceder al micrófono");
    }
  };
  
  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };
  
  // Process voice input (Speech-to-Text)
  const processVoiceInput = async (audioBlob) => {
    setLoadingIA(true);
    toast.loading("Procesando tu voz...");
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      const response = await axios.post(`${API}/voice/speech-to-text`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast.dismiss();
      
      if (response.data.success && response.data.text) {
        // Set the transcribed text and submit
        setUserInput(response.data.text);
        // Auto-submit the transcribed text
        await handleAsistenteSubmitWithText(response.data.text);
      } else {
        toast.error("No se pudo transcribir el audio");
      }
    } catch (error) {
      toast.dismiss();
      console.error("Error processing voice:", error);
      toast.error("Error procesando el audio");
    } finally {
      setLoadingIA(false);
    }
  };
  
  // Submit with specific text (for voice input)
  const handleAsistenteSubmitWithText = async (texto) => {
    if (!texto.trim()) return;
    
    setConversacion(prev => [...prev, { tipo: "user", texto }]);
    setLoadingIA(true);
    setUserInput("");
    
    try {
      const response = await axios.post(`${API}/asistente-ia`, {
        pregunta: texto,
        empresa_id: null,
        historial: conversacion.slice(-10)
      });
      
      const respuestaTexto = response.data.respuesta;
      
      setConversacion(prev => [...prev, { 
        tipo: "asistente", 
        texto: respuestaTexto 
      }]);
      
      // Auto-play response if voice is enabled
      if (voiceEnabled) {
        await playTextToSpeech(respuestaTexto);
      }
      
      // Handle navigation
      if (response.data.accion?.tipo === "navegar") {
        const ruta = response.data.accion.ruta;
        const destino = response.data.accion.destino;
        
        if (ruta !== "/" && empresas.length > 0) {
          cambiarEmpresa(empresas[0].id);
        }
        
        setTimeout(() => {
          toast.success(`Yendo a ${destino}...`);
          navigate(ruta);
        }, 1500);
      }
      
    } catch (error) {
      console.error("Error en asistente:", error);
      setConversacion(prev => [...prev, { 
        tipo: "asistente", 
        texto: "Disculpá, tuve un problema. ¿Podés intentar de nuevo?" 
      }]);
    } finally {
      setLoadingIA(false);
    }
  };
  
  // Play text as speech (Text-to-Speech) - OPTIMIZADO PARA BAJA LATENCIA
  const playTextToSpeech = async (text) => {
    // Limpiar texto para mejor pronunciación
    let cleanText = text
      .replace(/[\u{1F600}-\u{1F64F}]/gu, '')  // emoticons
      .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')  // symbols & pictographs
      .replace(/[\u{1F680}-\u{1F6FF}]/gu, '')  // transport & map
      .replace(/[\u{2600}-\u{26FF}]/gu, '')    // misc symbols
      .replace(/[\u{2700}-\u{27BF}]/gu, '')    // dingbats
      .replace(/\*\*/g, '')     // quitar negritas markdown
      .replace(/\*/g, '')       // quitar asteriscos
      .replace(/#/g, '')        // quitar hashtags
      .replace(/•/g, ', ')      // bullets a comas
      .replace(/\n+/g, '. ')    // saltos de línea a puntos
      .replace(/\s+/g, ' ')     // múltiples espacios a uno
      .replace(/Bs\s*/gi, 'bolivianos ')
      .replace(/\$/g, 'dólares ')
      .replace(/USD/gi, 'dólares')
      .trim();
    
    // Limitar longitud para respuesta MÁS RÁPIDA (máx 400 chars)
    const maxLength = 400;
    if (cleanText.length > maxLength) {
      const cutPoint = cleanText.lastIndexOf('.', maxLength);
      if (cutPoint > maxLength * 0.6) {
        cleanText = cleanText.substring(0, cutPoint + 1);
      } else {
        cleanText = cleanText.substring(0, maxLength) + ".";
      }
    }
    
    if (!cleanText || cleanText.length < 5) return;
    
    try {
      setIsPlayingAudio(true);
      
      const response = await axios.post(`${API}/voice/text-to-speech`, {
        text: cleanText,
        voice: "Laura",
        speed: 1.0
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.audio_base64) {
        const audioSrc = `data:audio/mp3;base64,${response.data.audio_base64}`;
        const audio = new Audio(audioSrc);
        currentAudioRef.current = audio;
        
        // Reproducir INMEDIATAMENTE sin esperar precarga completa
        audio.play().catch(err => {
          console.error("Error playing audio:", err);
          setIsPlayingAudio(false);
        });
        
        audio.onended = () => {
          setIsPlayingAudio(false);
          currentAudioRef.current = null;
        };
        
        audio.onerror = () => {
          setIsPlayingAudio(false);
          currentAudioRef.current = null;
        };
      } else {
        setIsPlayingAudio(false);
      }
    } catch (error) {
      console.error("Error playing TTS:", error);
      setIsPlayingAudio(false);
    }
  };
  
  // Stop playing audio
  const stopAudio = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    setIsPlayingAudio(false);
  };
  
  // Toggle voice responses
  const toggleVoice = () => {
    setVoiceEnabled(!voiceEnabled);
    toast.info(voiceEnabled ? "Respuestas de voz desactivadas" : "Respuestas de voz activadas");
  };

  // ==================== SPEECH-TO-TEXT (Botón de micrófono inline) ====================
  const startListening = () => {
    // Verificar soporte de Web Speech API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      toast.error("Tu navegador no soporta reconocimiento de voz. Usa Chrome o Edge.");
      return;
    }
    
    try {
      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;
      
      recognition.lang = 'es-ES'; // Español
      recognition.continuous = false; // Solo una frase
      recognition.interimResults = true; // Mostrar resultados parciales
      
      recognition.onstart = () => {
        setIsListening(true);
        toast.info("🎤 Escuchando... Habla ahora", { duration: 2000 });
      };
      
      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        // Mostrar resultado parcial o final en el input
        if (finalTranscript) {
          setUserInput(prev => prev + (prev ? ' ' : '') + finalTranscript);
        } else if (interimTranscript) {
          // Mostrar preview mientras habla (opcional)
        }
      };
      
      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
        
        if (event.error === 'no-speech') {
          toast.info("No escuché nada. Intenta de nuevo.");
        } else if (event.error === 'not-allowed') {
          toast.error("Permiso de micrófono denegado. Habilítalo en tu navegador.");
        } else {
          toast.error("Error de reconocimiento de voz. Intenta de nuevo.");
        }
      };
      
      recognition.onend = () => {
        setIsListening(false);
      };
      
      recognition.start();
      
    } catch (error) {
      console.error("Error starting speech recognition:", error);
      toast.error("Error al iniciar reconocimiento de voz");
      setIsListening(false);
    }
  };
  
  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  };
  
  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // ==================== END VOICE FUNCTIONS ====================

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-BO', {
      style: 'currency',
      currency: 'BOB',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const sugerencias = [
    "¿Cómo van las ventas de espirales esta semana?",
    "¿Cuántos packs mayoristas tenemos en stock?",
    "¿Quiénes son mis mejores clientes mayoristas?",
    "¿Cuándo llega la próxima importación de China?",
    "Genera un reporte para WhatsApp"
  ];

  // Solo SuperAdmin y Admin pueden usar el asistente IA
  const puedeUsarIA = user?.role === "superadmin" || user?.role === "admin";

  if (empresaLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <p className="text-xl text-slate-600">Preparando tu espacio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100" data-testid="home-page">
      {/* Header - Responsive */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-900 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-base sm:text-lg">E</span>
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-blue-900">EDMUNDO</h1>
              <p className="text-[10px] sm:text-xs text-slate-500 hidden sm:block">Tu centro de control</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Alertas */}
            {alertas.length > 0 && (
              <button
                onClick={() => setShowAlertas(!showAlertas)}
                className={`relative p-2 rounded-lg transition-colors touch-target ${
                  alertas.some(a => a.prioridad === "alta") 
                    ? "bg-red-100 text-red-600 hover:bg-red-200" 
                    : "bg-amber-100 text-amber-600 hover:bg-amber-200"
                }`}
                data-testid="alertas-btn"
              >
                <AlertTriangle size={18} />
                <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center">
                  {alertas.length}
                </span>
              </button>
            )}
            
            <div className="text-right hidden md:block">
              <p className="font-semibold text-slate-800">{user?.name}</p>
              <p className="text-sm text-slate-500 capitalize">{user?.role}</p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg sm:text-xl">{user?.name?.charAt(0)}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Alertas Dropdown - Responsive */}
      {showAlertas && alertas.length > 0 && (
        <div className="fixed top-14 sm:top-20 right-2 sm:right-4 md:right-8 w-[calc(100vw-16px)] sm:w-80 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 animate-fade-in max-w-[320px]">
          <div className="p-3 sm:p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 text-sm sm:text-base">
              <Zap size={16} className="text-amber-500" />
              Alertas del Sistema
            </h3>
            <button onClick={() => setShowAlertas(false)} className="p-1 hover:bg-slate-100 rounded">
              <X size={18} />
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {alertas.map((alerta, idx) => (
              <button
                key={idx}
                onClick={() => handleAlertaAccion(alerta)}
                className={`w-full p-4 text-left border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                  alerta.prioridad === "alta" ? "bg-red-50/50" :
                  alerta.prioridad === "media" ? "bg-amber-50/50" : ""
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 w-2 h-2 rounded-full ${
                    alerta.prioridad === "alta" ? "bg-red-500" :
                    alerta.prioridad === "media" ? "bg-amber-500" : "bg-green-500"
                  }`} />
                  <div>
                    <p className="font-semibold text-slate-800">{alerta.titulo}</p>
                    <p className="text-sm text-slate-600 mt-1">{alerta.mensaje}</p>
                    {alerta.accion && (
                      <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                        Click para ver <ArrowRight size={12} />
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* Bienvenida Hero - Responsive */}
        <section className="mb-6 sm:mb-8 animate-fade-in">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <GreetingIcon size={24} className="text-amber-500 sm:w-8 sm:h-8" />
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-slate-900">
              {greeting.text}, <span className="text-blue-900">{user?.name?.split(' ')[0]}</span>
            </h2>
          </div>
          <p className="text-sm sm:text-lg lg:text-xl text-slate-600 max-w-2xl">
            Tu asistente está listo para ayudarte.
          </p>
        </section>

        {/* Asistente IA - Solo para SuperAdmin y Admin - Responsive */}
        {puedeUsarIA && (
        <section className="mb-6 sm:mb-10">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Header del asistente - Responsive */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-3 sm:p-5 flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                  <Sparkles size={20} className="text-white sm:w-6 sm:h-6" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-lg font-bold text-white">Asistente IA</h3>
                  <p className="text-blue-200 text-[10px] sm:text-sm hidden sm:block">Contador + Data Scientist + Asesor</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Botón Modo Voz */}
                <button
                  onClick={() => setShowVoiceMode(true)}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors font-medium text-xs sm:text-sm"
                  data-testid="open-voice-mode"
                >
                  <AudioWaveform size={16} />
                  <span className="hidden sm:inline">Modo voz</span>
                </button>
                <button
                  onClick={clearChat}
                  className="text-blue-200 hover:text-white text-xs sm:text-sm font-medium p-2"
                >
                  <X size={18} className="sm:hidden" />
                  <span className="hidden sm:inline">Reiniciar</span>
                </button>
              </div>
            </div>

            {/* Sugerencias rápidas - Responsive */}
            <div className="px-3 sm:px-4 py-2 sm:py-3 bg-slate-50 border-b border-slate-200 flex gap-2 overflow-x-auto hide-scrollbar">
              {sugerencias.slice(0, 3).map((sug, idx) => (
                <button
                  key={idx}
                  onClick={() => setUserInput(sug)}
                  className="flex-shrink-0 px-2 sm:px-3 py-1 sm:py-1.5 bg-white border border-slate-200 rounded-full text-xs sm:text-sm text-slate-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-900 transition-colors"
                >
                  {sug.length > 35 ? sug.substring(0, 35) + '...' : sug}
                </button>
              ))}
            </div>

            {/* Conversación - Responsive */}
            <div className="p-3 sm:p-6 min-h-[200px] sm:min-h-[280px] max-h-[300px] sm:max-h-[400px] overflow-y-auto scrollbar-thin bg-slate-50/50">
              {conversacion.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex gap-3 mb-4 animate-fade-in ${msg.tipo === "user" ? "flex-row-reverse" : ""}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.tipo === "user" ? "bg-slate-200" : "bg-blue-100"
                  }`}>
                    {msg.tipo === "user" ? (
                      <span className="font-bold text-slate-600 text-sm">{user?.name?.charAt(0)}</span>
                    ) : (
                      <Sparkles size={18} className="text-blue-900" />
                    )}
                  </div>
                  <div className={`rounded-2xl p-4 shadow-sm max-w-[80%] ${
                    msg.tipo === "user" 
                      ? "bg-blue-900 text-white rounded-tr-none" 
                      : "bg-white border border-slate-100 rounded-tl-none"
                  }`}>
                    <p className={`text-base leading-relaxed whitespace-pre-wrap ${
                      msg.tipo === "user" ? "text-white" : "text-slate-800"
                    }`}>
                      {msg.texto}
                    </p>
                  </div>
                </div>
              ))}

              {loadingIA && (
                <div className="flex gap-3 mb-4">
                  <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center">
                    <Sparkles size={18} className="text-blue-900" />
                  </div>
                  <div className="bg-white rounded-2xl rounded-tl-none p-4 shadow-sm border border-slate-100">
                    <div className="flex gap-1.5">
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: "0ms"}}></span>
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: "150ms"}}></span>
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: "300ms"}}></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input del chat */}
            <form onSubmit={handleAsistenteSubmit} className="p-4 border-t border-slate-200 bg-white">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder={isListening ? "🎤 Escuchando..." : "Preguntá lo que quieras sobre tu negocio..."}
                  className={`flex-1 h-12 px-5 bg-slate-50 border-2 ${isListening ? 'border-red-400 bg-red-50' : 'border-slate-200'} focus:border-blue-900 rounded-xl text-base transition-colors outline-none`}
                  data-testid="asistente-input"
                />
                
                {/* Botón de micrófono */}
                <button
                  type="button"
                  onClick={toggleListening}
                  className={`h-12 w-12 flex items-center justify-center rounded-xl font-semibold transition-all ${
                    isListening 
                      ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' 
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-800'
                  }`}
                  title={isListening ? "Detener" : "Hablar para escribir"}
                  data-testid="mic-button"
                >
                  {isListening ? <MicOff size={20} /> : <Mic size={20} />}
                </button>
                
                <button
                  type="submit"
                  disabled={loadingIA || !userInput.trim()}
                  className="h-12 px-5 bg-blue-900 hover:bg-blue-950 disabled:bg-slate-300 text-white rounded-xl font-semibold flex items-center gap-2 transition-colors"
                  data-testid="asistente-send"
                >
                  <Send size={18} />
                  <span className="hidden md:inline">Enviar</span>
                </button>
              </div>
              
              <p className="mt-2 text-xs text-slate-400 flex items-center gap-2">
                <Mic size={14} />
                Toca el micrófono para dictar • &quot;Modo voz&quot; para conversar hablando
              </p>
            </form>
          </div>
        </section>
        )}

        {/* Voice Mode Modal */}
        <VoiceMode 
          isOpen={showVoiceMode} 
          onClose={() => setShowVoiceMode(false)}
          token={token}
          userName={user?.name}
        />

        {/* Empresas Grid - Responsive */}
        <section>
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-slate-900">Tus Empresas</h3>
            <span className="text-sm text-slate-500">{empresas.length} empresa{empresas.length !== 1 ? 's' : ''}</span>
          </div>

          {empresas.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 sm:p-12 text-center">
              <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
              <h4 className="text-lg sm:text-xl font-bold text-slate-800 mb-2">Sin empresas asignadas</h4>
              <p className="text-sm sm:text-base text-slate-500">Contacta al administrador</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
              {empresas.map((empresa) => {
                const empresaConMetricas = empresasData.find(e => e.id === empresa.id);
                const metricas = empresaConMetricas?.metricas;
                const colorPrincipal = empresa.color_principal || "#1e3a8a";
                
                return (
                  <div
                    key={empresa.id}
                    className="bg-white rounded-xl sm:rounded-2xl border-2 border-slate-200 hover:shadow-xl transition-all duration-300 group overflow-hidden active:scale-[0.98]"
                    style={{ 
                      '--empresa-color': colorPrincipal,
                      borderColor: 'rgb(226 232 240)',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = colorPrincipal}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgb(226 232 240)'}
                  >
                    {/* Card Content */}
                    <button
                      onClick={() => handleSelectEmpresa(empresa)}
                      className="w-full p-4 sm:p-6 text-left"
                      data-testid={`empresa-card-${empresa.id}`}
                    >
                      <div className="flex items-start justify-between mb-3 sm:mb-5">
                        {/* Logo o Icono - Responsive */}
                        {empresa.logo_url ? (
                          <div 
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform overflow-hidden bg-white border border-slate-200"
                          >
                            <img 
                              src={empresa.logo_url} 
                              alt={`Logo ${empresa.nombre}`}
                              className="w-full h-full object-contain p-1"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div 
                              className="w-full h-full items-center justify-center hidden"
                              style={{ backgroundColor: colorPrincipal }}
                            >
                              <Building2 size={24} className="text-white" />
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform"
                            style={{ background: `linear-gradient(135deg, ${colorPrincipal}, ${colorPrincipal}dd)` }}
                          >
                            <Building2 size={24} className="text-white" />
                          </div>
                        )}
                        <ArrowRight size={20} className="text-slate-300 group-hover:translate-x-1 transition-all" style={{ color: 'rgb(203 213 225)' }} />
                      </div>

                      <h4 
                        className="text-lg sm:text-xl font-bold text-slate-900 mb-1 transition-colors group-hover:text-blue-900 truncate"
                        style={{ '--hover-color': colorPrincipal }}
                      >
                        {empresa.nombre}
                      </h4>
                      <p className="text-slate-500 text-sm mb-1">
                        {empresa.descripcion || empresa.direccion || empresa.pais}
                      </p>
                      <p className="text-xs text-slate-400 flex items-center gap-1.5 mb-4">
                        <span 
                          className="w-2 h-2 rounded-full animate-pulse bg-green-500"
                        ></span>
                        Activo • {empresa.pais}
                      </p>

                      {metricas && (
                        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-slate-100">
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Ventas Hoy</p>
                            <p className="font-bold text-sm" style={{ color: colorPrincipal }}>{formatCurrency(metricas.ventas?.hoy)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Stock</p>
                            {/* Fix 26 (2026-08-31): muestra el stock real en cajas.
                                Antes mostraba "OK" o el contador de productos bajo mínimo. */}
                            <p className={`font-bold text-sm ${metricas.stock_critico?.cantidad > 0 ? "text-amber-600" : "text-slate-900"}`}>
                              {(metricas.stock_total ?? 0).toLocaleString("es-BO")}
                              <span className="font-normal text-slate-500"> cajas</span>
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Clientes</p>
                            <p className="font-bold text-slate-900 text-sm">{metricas.clientes?.total || 0}</p>
                          </div>
                        </div>
                      )}
                    </button>
                    
                    {/* Acciones rápidas */}
                    <div className="px-6 pb-4 flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGenerarReporte(empresa.id);
                        }}
                        className="flex-1 flex items-center justify-center gap-2 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 text-sm font-medium transition-colors"
                        data-testid={`reporte-btn-${empresa.id}`}
                      >
                        <FileText size={16} />
                        <span>Reporte PDF</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="mt-12 py-6 border-t border-slate-200 bg-white/50">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-500">
            EDMUNDO ERP+CRM • Tu negocio bajo control
          </p>
        </div>
      </footer>
    </div>
  );
}
